/* Custom hooks encapsulating admin auth + data fetching. */
import { useCallback, useEffect, useState } from "react";
import {
    adminApi,
    adminLogin,
    adminLogout,
    isAdminAuthenticated,
} from "@/components/Admin/api";

/**
 * useAdminAuth
 * — Detects existing cookie session on mount, exposes login/logout actions.
 * State machine: 'checking' → 'authed' | 'anon' (with login error string).
 */
export function useAdminAuth() {
    const [status, setStatus] = useState("checking");
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const ok = await isAdminAuthenticated();
                if (!cancelled) setStatus(ok ? "authed" : "anon");
            } catch {
                if (!cancelled) setStatus("anon");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const login = useCallback(async (token) => {
        setBusy(true);
        setError(null);
        try {
            await adminLogin(token);
            setStatus("authed");
            return true;
        } catch (err) {
            if (err?.response?.status === 401) {
                setError("Invalid admin token.");
            } else if (err?.response?.status === 503) {
                setError(
                    "Admin endpoints disabled. Set ADMIN_TOKEN in backend .env."
                );
            } else {
                setError(err?.message || "Unable to authenticate.");
            }
            return false;
        } finally {
            setBusy(false);
        }
    }, []);

    const logout = useCallback(async () => {
        await adminLogout();
        setStatus("anon");
    }, []);

    return { status, error, busy, login, logout, setError };
}

/**
 * useAdminData
 * — Fetches stats + submissions + webhook log + email log in parallel.
 * — Re-fetches when `filter` changes.
 * — Exposes `refresh` for the manual button and a `setLogs` updater used
 *    after the admin fires test events (so the panel updates immediately).
 */
export function useAdminData(authed, filter) {
    const [stats, setStats] = useState(null);
    const [rows, setRows] = useState([]);
    const [webhookLog, setWebhookLog] = useState([]);
    const [emailLog, setEmailLog] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        if (!authed) return;
        setLoading(true);
        setError(null);
        try {
            const [s, r, wl, el] = await Promise.all([
                adminApi.get("/stats"),
                adminApi.get("/submissions", { params: { type: filter, limit: 200 } }),
                adminApi.get("/webhook/log", { params: { limit: 25 } }),
                adminApi.get("/email/log", { params: { limit: 25 } }),
            ]);
            setStats(s.data);
            setRows(r.data || []);
            setWebhookLog(wl.data || []);
            setEmailLog(el.data || []);
        } catch (err) {
            setError(err?.message || "Unable to load admin data.");
        } finally {
            setLoading(false);
        }
    }, [authed, filter]);

    useEffect(() => {
        if (authed) refresh();
    }, [authed, refresh]);

    return {
        stats,
        rows,
        webhookLog,
        emailLog,
        loading,
        error,
        refresh,
        setStats,
        setWebhookLog,
        setEmailLog,
    };
}
